import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    FilledCard,
    Heading2,
    HorizontalDivider,
    Stack,
    useDisableBackButton,
} from '@myphx-engineering/phx-design-system';

import {
    orfoDrugKey,
    zepboundKwikpenDrugKey,
    zepboundPenDrugKey,
    zepboundVialDrugKey,
} from '../../../constants';
import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import { type DigitallyReceivedPrescriberOrderInfoFragment } from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { getDrugConfigByMedication } from '../../../util/config/get-drug-config-by-medication';
import { getPricing } from '../../../util/config/get-pricing';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { Prescriber } from '../../common/Prescriber';
import { ProviderInfo } from '../../provider/ProviderInfo';
import { ConditionalComponent } from '../../utility/ConditionalComponent';
import { MedicationPricingInfo } from '../order-details/MedicationPricingInfo';

import { OrderConfirmationHeader } from './shared/OrderConfirmationHeader';
import { OrderPrescriptionDetails } from './shared/OrderPrescriptionDetails';
import { OuraPromotionalModule } from './shared/OuraPromotionalModule';
import { TheresMoreCard } from './shared/TheresMoreCard';

export type OrderConfirmationProps = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

const OrderConfirmation = ({ prescriberOrder }: OrderConfirmationProps) => {
    const { t } = useTranslation('lilly');
    const drugProgram = useDrugProgram();
    const { enableTempLiteFlow } = useFlags();
    const configOverride = useMedicationConfigOverride();
    const prescriber = getFragment(prescriberOrder.prescriber);
    const provider = getFragment(prescriberOrder.assignedPharmacyProvider);
    const medication = getFragment(prescriberOrder?.authorizedMedication);

    useDisableBackButton(true);

    if (!medication || !provider) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const pricingInfo = getPricing(configOverride, medication);
    const drugConfig = getDrugConfigByMedication(drugProgram, medication);

    const isOrfo = drugConfig?.drugKey === orfoDrugKey;
    const showPricing = !!(
        pricingInfo &&
        (drugConfig?.drugKey === zepboundPenDrugKey ||
            drugConfig?.drugKey === zepboundVialDrugKey ||
            drugConfig?.drugKey === zepboundKwikpenDrugKey ||
            (enableTempLiteFlow && isOrfo))
    );

    return (
        <Stack data-testid="order-confirmation-page-root">
            <OrderConfirmationHeader
                prescriber={prescriber}
                prescriberOrder={prescriberOrder}
                provider={provider}
            />

            <HorizontalDivider />

            <Stack gap="sm">
                <Heading2>{t('orderConfirmation.pharmacy.header')}</Heading2>
                <ProviderInfo provider={provider} />
            </Stack>

            <ConditionalComponent condition={showPricing}>
                <Stack gap="sm">
                    <Heading2>{t('orderConfirmation.pricing.header')}</Heading2>
                    <FilledCard
                        color="primary"
                        data-testid="pharmacy-pricing-card"
                    >
                        <MedicationPricingInfo
                            pricingInfo={pricingInfo!}
                            medication={medication}
                        />
                    </FilledCard>
                </Stack>
            </ConditionalComponent>

            <OrderPrescriptionDetails prescriberOrder={prescriberOrder} />

            <Prescriber prescriber={prescriber} />

            <OuraPromotionalModule prescriberOrder={prescriberOrder} />

            <TheresMoreCard />
        </Stack>
    );
};

export default OrderConfirmation;
